import { getDomain } from '@/helpers/getDomains';
import type { ChatMessageSourcePayload } from '@/models/analytics/features/chat';
import { getXClientHeaders } from '@/utils/http/clientIdentityHeaders';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { logger } from '@/utils/logger/logger';
import { getTempUserId } from '@/utils/tempUser';
import { fetchEventSource } from '@fortaine/fetch-event-source';
import type {
  CapacityChunk,
  ChatMetadataChunkUnion,
  ChatMode,
  Chunk,
  EvidenceSourceFilters,
  SearchMetadataChunk,
} from '@heidi/evidence/contracts';
import { decodeAnswerPayload } from '@heidi/evidence/contracts';
import { getWorkspaceIdHeader } from '@heidi/workspace/data';
import {
  CONTENT_SECURITY_ERROR,
  isUserRefBannedErrorResponse,
  TIMELINE_STREAM_ERROR,
} from './chatErrors';
import {
  abortStreamWithReason,
  deriveAbortReason,
  STREAM_ABORT_REASON,
  type StreamAbortReason,
} from './streamAbortReason';

export {
  CONTENT_SECURITY_ERROR,
  isUserRefBannedErrorResponse,
  TIMELINE_STREAM_ERROR,
  USER_REF_BAN_ERROR,
} from './chatErrors';

/**
 * Integration partner configuration for API request (EVI-678).
 */
export interface IntegrationPartnerRequest {
  partner: string;
  enabled: boolean;
  max_results?: number;
}

export interface SlideContextPayload {
  presentation_id: string;
  slide_id: string;
  slide_index: number;
  title: string;
  all_slides?: Array<{ slide_id: string; slide_index: number; title: string }>;
  source?: 'generated' | 'imported';
  extracted_text?: string;
  shape_map?: Array<{
    index: number;
    name: string;
    text: string;
    placeholder_type?: string;
  }>;
}

interface TimelineStreamOptions {
  messages: Array<{ role: string; content: string }>;
  sessionId: string | null | undefined;
  patientProfileId?: string | null;
  chatId?: string | null;
  mode?: ChatMode;
  version?: number; // Evidence mode version: 1 (GPT-5 + LangChain) or 2 (Anthropic native)
  content?: string;
  domains?: string[];
  files?: File[]; // Legacy: raw files to upload with message (deprecated)
  attachmentIds?: string[]; // Pre-uploaded attachment IDs from /chat/attachments
  includeUserDocs?: boolean; // Whether to search user-uploaded documents
  includeWebSearch?: boolean; // Whether to include web search results (false = library-only mode)
  evidenceSourceFilters?: EvidenceSourceFilters; // Array of enabled evidence source filters (EVI-493)
  sourceListIds?: string[]; // IDs of user's web collections (source lists) for filtered search
  userDocCollectionIds?: string[]; // IDs of user's document collections to filter search
  collectionIds?: string[]; // Unified collection IDs — resolves both documents AND domains
  integrationPartners?: IntegrationPartnerRequest[]; // EVI-678: Integration partner configurations
  /**
   * User preference from the credits-remaining popover. When `true`, Heidi may
   * automatically invoke advanced clinical AI tools that consume credits. When
   * `false`, the user has opted out of automatic credit-consuming tool use.
   * Sent to `/chat/completions` as `use_credits_when_needed`.
   */
  useCreditsWhenNeeded?: boolean;
  isInSession?: boolean; // Whether this is an in-session Ask Heidi request (uses ask_ai quota)
  country?: string; // Country name (e.g. "Australia") for localizing evidence search for unauthenticated users
  slideContext?: SlideContextPayload; // Targeted slide for editing in Slide Builder
  formTemplateId?: string; // Template ID for PDF form filling (from template picker)
  activeTab?: string; // Active tab type: "NOTE" or "DOCUMENT" — tells backend which tab the user is viewing
  activeDocumentId?: string; // Document ID when activeTab is "DOCUMENT" — for resolving the correct template
  panelWidth?: number; // Evidence chat panel width in CSS pixels
  panelHeight?: number; // Evidence chat panel visible height in CSS pixels
  shareToken?: string; // Link-share token for unauthenticated session access
  candidateId?: string; // SBS experiment candidate ID — routes to alternative model on backend
  /**
   * Analytics-only source metadata describing how the last user message was
   * initiated. Persisted to MongoDB on the user message for chip/shortcut
   * attribution. Never used for routing/authorization.
   */
  source?: ChatMessageSourcePayload;
  onChunk: (chunk: Chunk) => void;
  onKeepalive?: () => void; // Called on heartbeat/processing events so consumers can reset their own timers
  onMetadata?: (metadata: ChatMetadataChunkUnion | SearchMetadataChunk) => void;
  onCapacity?: (chunk: CapacityChunk) => void;
  onComplete: (info: StreamCloseInfo) => void;
  /**
   * `context` carries the lifecycle snapshot + FE-origin cause classification
   * for the failure. Optional so existing single-arg consumers stay valid; the
   * stream always supplies it.
   */
  onError: (error: Error, context?: StreamErrorContext) => void;
  /**
   * Fires exactly once, and only on the "settled but nobody was told" gap:
   * the SSE promise resolved without a terminal having been delivered to
   * `onComplete`/`onError`. `@fortaine/fetch-event-source` silently resolves on
   * an abort signal without invoking `onclose`/`onerror`, and can resolve
   * without `onclose` at all — in the wild that leaves a send with no terminal
   * telemetry until the 5-min global watchdog fires. This hook lets
   * the consumer emit a terminal RUM report immediately for that gap. It never
   * fires on the normal close/error paths, so it can never double-report.
   */
  onStreamSettled?: (lifecycle: StreamLifecycle) => void;
  abortController: AbortController;
}

/**
 * Which exit path settled the SSE stream. `onclose`/`onerror`/`idle_timeout`
 * already deliver a terminal to the consumer; `abort`/`silent_resolve` are the
 * gap paths reported only via `onStreamSettled`.
 */
export type StreamCloseReason =
  | 'onclose'
  | 'onerror'
  | 'idle_timeout'
  | 'abort'
  | 'silent_resolve';

// Abort-reason vocabulary + helpers live in a dependency-free module (imported
// above) so light hooks can stamp a reason without importing this heavy graph.
// Re-exported here so existing `@/utils/timelineEventSource` importers keep working.
export { abortStreamWithReason, deriveAbortReason, STREAM_ABORT_REASON };
export type { StreamAbortReason };

/**
 * Snapshot of the SSE stream's lifecycle at the moment it settled. Attached to
 * every terminal RUM report so a send's outcome can be classified in Datadog
 * within seconds — e.g. the incident signature is `chunksReceived: 1` +
 * `lastChunkType: 'message_info'` + a gap `closeReason`, meaning the client
 * saw only the opening chunk then went silent.
 */
export interface StreamLifecycle {
  /** How the stream settled. */
  closeReason: StreamCloseReason;
  /** SSE messages with a payload received (incl. heartbeats/metadata/sentinel). */
  chunksReceived: number;
  /** `type` of the last SSE message parsed, or undefined if none arrived. */
  lastChunkType?: string;
  /** Backend sent the terminal sentinel (`stream_complete`). */
  sawStreamComplete: boolean;
  /** Backend advertised terminal-sentinel support in `message_info`. */
  supportsTerminalSentinel: boolean;
  /** Capable stream settled without its sentinel — cut off mid-answer. */
  truncated: boolean;
  /** Wall-clock ms since the last SSE event was handled on the main thread. */
  msSinceLastEvent: number;
  /** Page was backgrounded at settle time (main-thread timers unreliable). */
  documentHidden: boolean;
  /**
   * Bounded, PHI-safe cause of an abort (user cancel vs supersede vs retry vs
   * global timeout), read losslessly from the native `AbortSignal.reason`.
   * Present only when the stream was aborted with a known reason; `undefined`
   * for a clean close, an error, or a bare abort. RUM-only — deliberately absent
   * from the Snowplow `evidence_chat_error` event, so it needs no schema change.
   */
  abortReason?: StreamAbortReason;
}

/**
 * How a stream closed cleanly (SSE `onclose`), passed to `onComplete`.
 *
 * `truncated` is `true` only when the backend advertised terminal-sentinel
 * support (via `message_info.supports_terminal_sentinel`) but the stream closed
 * without the trailing `stream_complete` sentinel — i.e. the answer was cut off
 * mid-flight (ALB idle-timeout, worker SIGTERM, proxy close) rather than
 * finishing. On pre-sentinel backends the capability is never advertised, so
 * `truncated` stays `false` and the turn is assumed successful (EVI-2117).
 */
export interface StreamCloseInfo {
  truncated: boolean;
  /**
   * Monotonic `performance.now()` at which the last SSE event was handled. Main
   * thread timestamp, so a throttled/backgrounded page inflates it — it is the
   * lag-susceptible counterpart to `networkTiming.responseEnd`, useful both as a
   * fallback and as a direct measure of how much lag a turn suffered.
   */
  lastEventTime?: number;
  /**
   * Resolves once resource timing for the request lands, or once the grace
   * window lapses without it.
   *
   * Deliberately handed over unresolved: `onComplete` fires immediately so the
   * UI can settle the finished turn, and consumers await this only to attach
   * telemetry. Measurement must never sit on the critical path.
   */
  networkTiming: Promise<NetworkTimingCaptureResult>;
  /**
   * Lifecycle snapshot at close (`closeReason: 'onclose'`), so the clean-close
   * terminal RUM report carries the same counters as the gap-path report.
   */
  lifecycle: StreamLifecycle;
}

/**
 * PHI-safe, bounded discriminator for *why* a chat stream failed — the single
 * cause vocabulary carried on every terminal RUM report and on the
 * `evidence_chat_error` Snowplow event. These are the causes the backend often
 * *cannot* see: when a stream drops after the backend already flushed a
 * `200`/`outcome:success`, no non-success span is retained (the #14917
 * retention filter only keeps `outcome != success`), so this code is the only
 * signal for the true proximate fault. It collapses the many "Sorry, something
 * went wrong" strings into a stable enum and, crucially, names the failure
 * modes that never carry an HTTP status (network drop, idle timeout, client
 * abort, parse error). Complements the raw `httpStatus` number.
 */
export type EvidenceStreamErrorCode =
  | 'bad_request' // 400
  | 'unauthenticated' // 401
  | 'auth_failed' // 403
  | 'not_found' // 404
  | 'rate_limited' // 429 or in-band rate_limit_exceeded
  | 'concurrency_limited' // in-band concurrency_limit_exceeded
  | 'server_error' // 5xx
  | 'prompt_injection' // 400 content-security refusal
  | 'user_ref_banned' // 403 user-ref ban
  | 'idle_timeout' // no SSE activity for STREAM_IDLE_TIMEOUT_MS
  | 'client_abort' // AbortError (user cancel / new send)
  | 'parse' // SyntaxError decoding a chunk
  | 'network' // fetch/DNS/TLS/offline — no HTTP response reached us
  | 'unknown';

/**
 * The Error surfaced from a throw site, carrying the numeric HTTP status the
 * `onopen` handler stamped on it. Downstream classification reads this bare
 * number instead of re-parsing `error.message`, which may embed a response body
 * (PHI).
 */
export interface ClassifiedStreamError extends Error {
  httpStatus?: number;
  /** Seconds from a `Retry-After` header. Never a response body. */
  retryAfterSeconds?: number;
}

/**
 * Diagnostic context handed to `onError` alongside the humanized message, so a
 * failure with no retained backend trace can still be pinpointed from RUM
 * alone: `lifecycle` says *where/when* the stream died (chunk counts, timing,
 * terminal-sentinel state), and `errorCode`/`errorName`/`httpStatus` say
 * *what* killed it. The consumer's humanized `error.message` alone collapses
 * every unclassified failure into "Sorry, something went wrong" — this restores
 * the lost signal.
 */
export interface StreamErrorContext {
  /** Lifecycle snapshot at the moment the error settled the stream. */
  lifecycle: StreamLifecycle;
  /** PHI-safe semantic cause code — the single "why" vocabulary. */
  errorCode: EvidenceStreamErrorCode;
  /** `name` of the underlying error (or its `cause`), when one exists. */
  errorName?: string;
  /** Parsed HTTP status when the failure was an HTTP response. */
  httpStatus?: number;
  /**
   * Wait the server asked for, from `Retry-After` or an in-band
   * `retry_after_seconds`. A number of seconds, never a message body.
   */
  retryAfterSeconds?: number;
  /**
   * Raw underlying error message, to sub-classify the opaque buckets — e.g.
   * `network` splits into `"Failed to fetch"` (Chromium) vs `"Load failed"` /
   * `"The network connection was lost"` (WebKit). Populated only for the
   * browser/library/our-code categories (`network`/`parse`/`client_abort`/
   * `unknown`) and **never** for the HTTP categories, whose message wraps the
   * server response body and could echo user input (PHI). Length-capped.
   */
  errorDetail?: string;
}

export type NetworkTimingCaptureStatus = 'captured' | 'timed_out' | 'unsupported';

interface ErrorResponse {
  detail?: string | { type?: string; msg?: string };
  error_code?: string;
  [key: string]: unknown;
}

/** Quota exceeded error types returned by the backend */
export const QUOTA_ERROR_TYPES = {
  ASK_AI: 'ASKAIQuotaExceedError',
  TEMPLATE: 'TemplateQuotaExceedError',
  LINKING: 'EvidencePatientOrSessionLinkedQuotaExceedError',
} as const;

export type QuotaErrorType = (typeof QUOTA_ERROR_TYPES)[keyof typeof QUOTA_ERROR_TYPES];

export type TimelineStreamErrorCode =
  | (typeof TIMELINE_STREAM_ERROR)[keyof typeof TIMELINE_STREAM_ERROR]
  | QuotaErrorType;

/** Identifiable error codes or other string messages from the timeline stream. */
export type TimelineStreamErrorMessage = string;

export interface NetworkTimingCaptureResult {
  status: NetworkTimingCaptureStatus;
  /**
   * `PerformanceResourceTiming.responseEnd` for the streaming request. The
   * browser timestamps this in the network stack, independently of when a
   * throttled page resumes processing the completed stream.
   */
  responseEnd?: number;
}

interface NetworkTimingCapture {
  finish: () => Promise<NetworkTimingCaptureResult>;
  cancel: () => void;
}

const NETWORK_TIMING_CAPTURE_GRACE_MS = 500;

function createNetworkTimingCapture(
  url: string,
  requestStartedAt: number
): NetworkTimingCapture {
  let observer: PerformanceObserver | null = null;
  let result: NetworkTimingCaptureResult | null = null;
  let graceTimeout: ReturnType<typeof setTimeout> | null = null;
  let resolvePending: ((result: NetworkTimingCaptureResult) => void) | null = null;

  const disconnect = () => {
    observer?.disconnect();
    observer = null;
    if (graceTimeout !== null) {
      clearTimeout(graceTimeout);
      graceTimeout = null;
    }
  };

  const settle = (nextResult: NetworkTimingCaptureResult) => {
    if (result !== null) return;

    result = nextResult;
    disconnect();
    resolvePending?.(nextResult);
    resolvePending = null;
  };

  if (typeof PerformanceObserver === 'undefined') {
    result = { status: 'unsupported' };
  } else {
    try {
      observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType !== 'resource' || entry.name !== url) continue;

          const resourceEntry = entry as PerformanceResourceTiming;
          if (
            resourceEntry.startTime >= requestStartedAt &&
            Number.isFinite(resourceEntry.responseEnd) &&
            resourceEntry.responseEnd >= resourceEntry.startTime
          ) {
            settle({
              status: 'captured',
              responseEnd: resourceEntry.responseEnd,
            });
            return;
          }
        }
      });
      observer.observe({ type: 'resource', buffered: false });
    } catch {
      disconnect();
      result = { status: 'unsupported' };
    }
  }

  return {
    finish: () => {
      if (result !== null) return Promise.resolve(result);

      return new Promise((resolve) => {
        resolvePending = resolve;
        graceTimeout = setTimeout(
          () => settle({ status: 'timed_out' }),
          NETWORK_TIMING_CAPTURE_GRACE_MS
        );
      });
    },
    cancel: disconnect,
  };
}

const NETWORK_ERROR_RE =
  /failed to fetch|networkerror|load failed|network request failed|network connection was lost/i;

export const getErrorMessage = (error: unknown): TimelineStreamErrorMessage => {
  if (!(error instanceof Error)) {
    return TIMELINE_STREAM_ERROR.QUERY_FAILED;
  }

  if (error.name === 'AbortError') {
    return 'Timeline stream was cancelled';
  }

  // Idle / soft timeout may arrive already stamped as QUERY_TIMEOUT.
  if (
    error.message === TIMELINE_STREAM_ERROR.QUERY_TIMEOUT ||
    error.message === 'The response took too long. Please try again.' ||
    error.message === 'This response seems stuck. You can retry your question.'
  ) {
    return TIMELINE_STREAM_ERROR.QUERY_TIMEOUT;
  }

  if (NETWORK_ERROR_RE.test(error.message)) {
    return TIMELINE_STREAM_ERROR.QUERY_NETWORK;
  }

  const leadingStatus = parseLeadingStatus(error.message);

  if (error.message.includes('403') || leadingStatus === 403) {
    try {
      const colonIndex = error.message.indexOf(': ');
      const jsonText =
        colonIndex !== -1
          ? error.message.substring(colonIndex + 2).trim()
          : error.message;
      const parsed = JSON.parse(jsonText) as ErrorResponse;

      if (isUserRefBannedErrorResponse(parsed)) {
        return TIMELINE_STREAM_ERROR.USER_REF_BANNED;
      }
    } catch {
      // If parsing fails, fall through to friendly query-failed copy.
    }

    return TIMELINE_STREAM_ERROR.QUERY_FAILED;
  }

  if (error.message.includes('429') || leadingStatus === 429) {
    return TIMELINE_STREAM_ERROR.RATE_LIMIT_EXCEEDED;
  }

  // All 5xx (and legacy "Server error …" strings) → plain-English failed sentinel.
  // Route on anchored leading status only — never substring-scan the body for "500"
  // (a 4xx JSON body containing that digit sequence must still reach quota/PROMPT_INJECTION).
  if (leadingStatus !== undefined && leadingStatus >= 500) {
    return TIMELINE_STREAM_ERROR.QUERY_FAILED;
  }

  if (error.message.includes('400') || leadingStatus === 400) {
    if (error.message.includes('cannot be processed as it may not comply')) {
      return 'Heidi does not diagnose, treat, mitigate, or alleviate any illnesses, diseases, or medical conditions.';
    }

    // Try to extract detail from JSON error response
    try {
      // Extract JSON after colon if present (format: "HTTP 400: {JSON}")
      const colonIndex = error.message.indexOf(': ');
      const jsonText =
        colonIndex !== -1
          ? error.message.substring(colonIndex + 2).trim()
          : error.message;

      const parsed = JSON.parse(jsonText) as ErrorResponse;

      // Preserve quota error types for paywall handling upstream
      if (typeof parsed.detail === 'object' && parsed.detail?.type) {
        const errorType = parsed.detail.type;
        if (
          errorType === QUOTA_ERROR_TYPES.ASK_AI ||
          errorType === QUOTA_ERROR_TYPES.TEMPLATE ||
          errorType === QUOTA_ERROR_TYPES.LINKING
        ) {
          return errorType;
        }

        if (errorType === CONTENT_SECURITY_ERROR.PROMPT_INJECTION) {
          return TIMELINE_STREAM_ERROR.PROMPT_INJECTION;
        }
      }

      // Do not pass arbitrary backend `detail` strings to the UI (PHI / clinical IDs).
      // Quota types and prompt-injection are handled above; everything else is QUERY_FAILED.
    } catch {
      // If parsing fails, fall through to default message
    }

    return TIMELINE_STREAM_ERROR.QUERY_FAILED;
  }

  // Other 4xx without a dedicated UI → friendly failed message (SWR-35).
  if (leadingStatus !== undefined && leadingStatus >= 400 && leadingStatus < 500) {
    return TIMELINE_STREAM_ERROR.QUERY_FAILED;
  }

  return TIMELINE_STREAM_ERROR.QUERY_FAILED;
};

// Raw error messages are short browser/library strings; cap defensively so a
// pathological message can't bloat the RUM payload.
const MAX_ERROR_DETAIL_LEN = 300;

const codeForStatus = (status: number): EvidenceStreamErrorCode => {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthenticated';
    case 403:
      return 'auth_failed';
    case 404:
      return 'not_found';
    case 429:
      return 'rate_limited';
    default:
      return status >= 500 ? 'server_error' : 'unknown';
  }
};

/**
 * Seconds until the client may send again. Accepts `retry_after_seconds` or a
 * `Retry-After` delta-seconds / HTTP-date. Missing, zero, and negative values
 * fall through so the caller can apply the default wait. Kept here so the
 * stream parser does not import chat.
 */
const parseRetryAfterSeconds = (value: unknown, now = Date.now()): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.ceil(value);
  }

  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return seconds > 0 ? Math.ceil(seconds) : undefined;
  }

  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;

  const seconds = Math.ceil((dateMs - now) / 1000);
  return seconds > 0 ? seconds : undefined;
};

const errorCodeFromInBandType = (
  errorType: string | undefined
): EvidenceStreamErrorCode => {
  switch (errorType) {
    case 'rate_limit_exceeded':
      return 'rate_limited';
    case 'concurrency_limit_exceeded':
      return 'concurrency_limited';
    default:
      return 'unknown';
  }
};

const readStampedRetryAfter = (error: unknown): number | undefined => {
  const candidates = [error, error instanceof Error ? error.cause : undefined];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue;
    const value = (candidate as ClassifiedStreamError).retryAfterSeconds;
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      return value;
    }
  }
  return undefined;
};

const classificationWithRetryAfter = (
  error: unknown,
  closeReason: StreamCloseReason
): Omit<StreamErrorContext, 'lifecycle'> => {
  const retryAfterSeconds = readStampedRetryAfter(error);
  return {
    ...classifyStreamError(error, closeReason),
    ...(retryAfterSeconds !== undefined && { retryAfterSeconds }),
  };
};

/**
 * Read the numeric status from the marker the throw sites prepend
 * (`HTTP 400: …` / `Server error 500: …`). Anchored to the start so a
 * status-like number inside a response body can never be captured.
 */
const parseLeadingStatus = (message: string): number | undefined => {
  const match = /^(?:HTTP|Server error) (\d{3})\b/.exec(message);
  return match ? Number(match[1]) : undefined;
};

/**
 * Classify a stream failure into the PHI-safe cause fields carried on the
 * terminal RUM report and the `evidence_chat_error` Snowplow event. Prefers the
 * underlying error's `name`/`message` (unwrapped from the humanized wrapper's
 * `cause`) so the proximate fault survives the `getErrorMessage()` copy
 * collapse. `idle_timeout` is passed explicitly because that path fabricates its
 * own message with no underlying error to inspect.
 *
 * Status resolution is PHI-safe: it prefers the bare number stamped at the
 * `onopen` throw site, then a start-anchored `HTTP <n>:`/`Server error <n>:`
 * marker — never a free-text scan of a body. `prompt_injection` / `user_ref_banned`
 * are decoded by `getErrorMessage` from *bounded* response fields (detail.type /
 * error_code), not by matching raw body text.
 */
export const classifyStreamError = (
  rawError: unknown,
  closeReason: StreamCloseReason
): {
  errorCode: EvidenceStreamErrorCode;
  errorName?: string;
  httpStatus?: number;
  errorDetail?: string;
} => {
  if (closeReason === 'idle_timeout') {
    return { errorCode: 'idle_timeout', errorName: 'IdleTimeoutError' };
  }

  const err = rawError instanceof Error ? rawError : undefined;
  const cause = err?.cause instanceof Error ? err.cause : undefined;
  // The original error carries the true name (SyntaxError/TypeError/AbortError);
  // the wrapper only holds the humanized copy.
  const errorName = cause?.name ?? err?.name;
  const withName = errorName !== undefined ? { errorName } : {};

  // Prefer the underlying cause message over the humanized wrapper copy — that's
  // where the diagnostic string lives (e.g. "Failed to fetch"). Only attached to
  // the browser/library/our-code codes below, never to HTTP ones (whose message
  // embeds the server response body and could echo user input / PHI).
  const rawDetail = (cause?.message ?? err?.message)
    ?.trim()
    .slice(0, MAX_ERROR_DETAIL_LEN);
  const withDetail = rawDetail ? { errorDetail: rawDetail } : {};

  if (errorName === 'AbortError') {
    return { errorCode: 'client_abort', ...withName, ...withDetail };
  }
  if (errorName === 'SyntaxError') {
    return { errorCode: 'parse', ...withName, ...withDetail };
  }

  // A clean numeric status the `onopen` throw site stamped — no body attached.
  const stampedStatus =
    rawError && typeof rawError === 'object'
      ? (rawError as ClassifiedStreamError).httpStatus
      : undefined;

  // prompt-injection (400) and user-ref-ban (403) are only distinguishable from
  // the response body, which `getErrorMessage` decodes from *bounded* fields.
  // No `errorDetail` here — that message wraps the body (PHI).
  const mapped = getErrorMessage(rawError);
  if (mapped === TIMELINE_STREAM_ERROR.PROMPT_INJECTION) {
    return {
      errorCode: 'prompt_injection',
      httpStatus: stampedStatus ?? 400,
      ...withName,
    };
  }
  if (mapped === TIMELINE_STREAM_ERROR.USER_REF_BANNED) {
    return {
      errorCode: 'user_ref_banned',
      httpStatus: stampedStatus ?? 403,
      ...withName,
    };
  }

  // Prefer the stamped status; else read a start-anchored marker off either the
  // wrapper or its cause. HTTP messages embed the response body, so no detail.
  const status =
    stampedStatus ??
    parseLeadingStatus(err?.message ?? '') ??
    parseLeadingStatus(cause?.message ?? '');
  if (status !== undefined) {
    return { errorCode: codeForStatus(status), httpStatus: status, ...withName };
  }

  // Browser network failures surface as TypeError with an opaque message and
  // never expose an HTTP status — the true hop is invisible to FE and BE alike.
  // errorDetail sub-classifies the bucket ("Failed to fetch" vs "Load failed").
  const message = `${err?.message ?? ''} ${cause?.message ?? ''}`;
  if (errorName === 'TypeError' || NETWORK_ERROR_RE.test(message)) {
    return { errorCode: 'network', ...withName, ...withDetail };
  }

  return { errorCode: 'unknown', ...withName, ...withDetail };
};

// If no SSE data arrives for this duration, abort the stream.
// Heartbeats from the backend arrive every ~10s during long operations,
// so 240s means 24 missed heartbeats before we give up.
const STREAM_IDLE_TIMEOUT_MS = 240_000;
const IDLE_CHECK_INTERVAL_MS = 10_000;

export const createTimelineStream = async (
  options: TimelineStreamOptions
): Promise<void> => {
  const {
    messages,
    sessionId,
    patientProfileId,
    chatId,
    mode,
    version,
    content,
    domains,
    files,
    attachmentIds,
    includeUserDocs,
    includeWebSearch,
    evidenceSourceFilters,
    sourceListIds,
    userDocCollectionIds,
    collectionIds,
    integrationPartners,
    useCreditsWhenNeeded,
    isInSession,
    country,
    slideContext,
    formTemplateId,
    activeTab,
    activeDocumentId,
    panelWidth,
    panelHeight,
    shareToken,
    candidateId,
    source,
    onChunk,
    onKeepalive,
    onMetadata,
    onCapacity,
    onComplete,
    onError,
    onStreamSettled,
    abortController,
  } = options;

  const baseUrl = getDomain('v2');
  const url = `${baseUrl}chat/completions`;

  const tempUserId = getTempUserId();

  // Build request body
  // Use FormData only when legacy files are present (deprecated path)
  // When attachmentIds is provided, use JSON (files already uploaded)
  const hasLegacyFiles = files && files.length > 0;
  const hasAttachmentIds = attachmentIds && attachmentIds.length > 0;

  const requestPayload = {
    messages,
    ...(sessionId !== null && sessionId !== undefined && { sessionId }),
    ...(patientProfileId && { patientProfileId }),
    ...(chatId && { chatId }),
    mode,
    ...(version !== undefined && { version }),
    content,
    domains,
    ...(includeUserDocs !== undefined && { include_user_docs: includeUserDocs }),
    ...(includeWebSearch !== undefined && { include_web_search: includeWebSearch }),
    ...(evidenceSourceFilters !== undefined && {
      evidence_source_filters: evidenceSourceFilters,
    }),
    ...(sourceListIds !== undefined &&
      sourceListIds.length > 0 && {
        source_list_ids: sourceListIds,
      }),
    // User document collection IDs for filtering library search
    ...(userDocCollectionIds !== undefined &&
      userDocCollectionIds.length > 0 && {
        user_doc_collection_ids: userDocCollectionIds,
      }),
    // Unified collection IDs — resolves both documents AND domains
    ...(collectionIds !== undefined &&
      collectionIds.length > 0 && {
        collection_ids: collectionIds,
      }),
    // EVI-678: Include integration partners when configured
    ...(integrationPartners !== undefined &&
      integrationPartners.length > 0 && {
        integration_partners: integrationPartners,
      }),
    // User preference for automatic credit-consuming tool use (credits popover toggle)
    ...(useCreditsWhenNeeded !== undefined && {
      use_credits_when_needed: useCreditsWhenNeeded,
    }),
    // Include pre-uploaded attachment IDs when available
    ...(hasAttachmentIds && { attachmentIds }),
    // In-session Ask Heidi flag for quota routing
    ...(isInSession && { isInSession: true }),
    // Country for localizing evidence search for unauthenticated users
    ...(country && { country }),
    // Slide context for targeted slide editing in Slide Builder
    ...(slideContext && { slide_context: slideContext }),
    // Template ID for form filling (from template picker)
    ...(formTemplateId && { form_template_id: formTemplateId }),
    // Active tab context for template/preferences resolution
    ...(activeTab && { activeTab }),
    ...(activeDocumentId && { activeDocumentId }),
    // Client window dimensions for analytics
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    // Evidence chat panel dimensions for responsive LLM formatting
    ...(panelWidth !== undefined && { panelWidth }),
    ...(panelHeight !== undefined && { panelHeight }),
    // Link-share token for unauthenticated session access
    ...(shareToken && { shareToken }),
    ...(candidateId && { candidateId }),
    // Analytics-only source metadata (chip/shortcut attribution + queued flag).
    // Backend treats this as client-asserted (analytics only, never used for
    // routing/auth). Callers (useChatSendMessage) always populate at least
    // `entry_method` and `queued`, so a non-null `source` is always meaningful.
    ...(source && { source }),
  };

  let body: FormData | string;
  let contentTypeHeader: Record<string, string>;

  if (hasLegacyFiles) {
    // Legacy path: upload files with message (deprecated)
    const formData = new FormData();
    formData.append('request', JSON.stringify(requestPayload));
    files.forEach((file) => {
      formData.append('files', file);
    });
    body = formData;
    // Don't set Content-Type for FormData - browser will set it with boundary
    contentTypeHeader = {};
  } else {
    // Modern path: files pre-uploaded, just send JSON with attachmentIds
    body = JSON.stringify(requestPayload);
    contentTypeHeader = { 'Content-Type': 'application/json' };
  }

  // Track last SSE activity (any message, including heartbeats) for idle timeout.
  let lastActivityAt = Date.now();
  let idleTimeoutChecker: ReturnType<typeof setInterval> | null = null;

  // EVI-2117 clean-close detection. `capabilityAdvertised` latches when the
  // backend's message_info declares it emits a terminal sentinel;
  // `sawTerminalSentinel` latches when that `stream_complete` chunk arrives. On
  // close, a capable stream that never sent the sentinel was truncated
  // mid-answer (infra idle-timeout / worker SIGTERM / proxy close) rather than
  // finishing — so it must not be reported as a success.
  let capabilityAdvertised = false;
  let sawTerminalSentinel = false;

  // Lifecycle counters surfaced on every terminal RUM report. They
  // let a stalled send be classified in Datadog within seconds: the incident
  // signature is a single `message_info` chunk then silence.
  let chunksReceived = 0;
  let lastChunkType: string | undefined;
  // Latches once a terminal has been handed to onComplete/onError. The finally
  // block uses it to detect the "settled but nobody was told" gap and fire
  // onStreamSettled exactly once for it (never on the normal close/error path).
  let terminalDelivered = false;

  let lastEventTime: number | undefined;

  // Mobile browsers freeze JS and kill connections when backgrounded.
  // Track hidden time so we don't false-trigger the idle timeout when
  // the user switches apps and comes back.
  let hiddenSince: number | null = null;

  const handleVisibilityChange = () => {
    if (document.hidden) {
      hiddenSince = Date.now();
    } else if (hiddenSince !== null) {
      // Shift lastActivityAt forward by the duration spent hidden so the
      // idle checker doesn't count background time as inactivity.
      const hiddenDuration = Date.now() - hiddenSince;
      lastActivityAt += hiddenDuration;
      hiddenSince = null;
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  const clearIdleTimeout = () => {
    if (idleTimeoutChecker !== null) {
      clearInterval(idleTimeoutChecker);
      idleTimeoutChecker = null;
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  const buildLifecycle = (closeReason: StreamCloseReason): StreamLifecycle => {
    const abortReason = deriveAbortReason(abortController.signal);
    return {
      closeReason,
      chunksReceived,
      ...(lastChunkType !== undefined && { lastChunkType }),
      sawStreamComplete: sawTerminalSentinel,
      supportsTerminalSentinel: capabilityAdvertised,
      truncated: capabilityAdvertised && !sawTerminalSentinel,
      msSinceLastEvent: Date.now() - lastActivityAt,
      documentHidden: document.hidden,
      ...(abortReason !== undefined && { abortReason }),
    };
  };

  // Periodically check for idle timeout. If the backend is alive it sends
  // heartbeats every ~10s during long-running operations, so 90s of silence
  // means something is truly stuck.
  idleTimeoutChecker = setInterval(() => {
    // Skip timeout checks while the page is hidden — the connection is
    // likely frozen by the OS and timers are unreliable.
    if (document.hidden) return;

    const elapsed = Date.now() - lastActivityAt;
    if (elapsed > STREAM_IDLE_TIMEOUT_MS) {
      clearIdleTimeout();
      logger.error('Chat timeline stream idle timeout', {
        elapsedMs: elapsed,
        sessionId: sessionId ?? 'sessionless',
      });
      // Deliver the terminal before aborting: the abort makes the SSE promise
      // resolve silently, so latching here keeps the finally block from also
      // reporting this exit via onStreamSettled.
      terminalDelivered = true;
      abortController.abort();
      const idleError = new Error(TIMELINE_STREAM_ERROR.QUERY_TIMEOUT);
      onError(idleError, {
        lifecycle: buildLifecycle('idle_timeout'),
        ...classifyStreamError(idleError, 'idle_timeout'),
      });
    }
  }, IDLE_CHECK_INTERVAL_MS);

  const requestStartedAt = performance.now();
  const networkTimingCapture = createNetworkTimingCapture(url, requestStartedAt);
  let closeFinalizationStarted = false;

  try {
    // Read the token inside the try, after the idle timer / lifecycle
    // scaffolding above are already live: an abort landing during this await
    // now falls through to the same `finally` below that reports a real
    // in-flight abort, instead of opening an unabortable connection with
    // `fetchEventSource` (its signal bridge only cancels a fetch already in
    // flight, not one requested after the signal already fired).
    const authToken = await getAuthTokenForHttpClient();
    if (abortController.signal.aborted) return;

    await fetchEventSource(url, {
      method: 'POST',
      headers: {
        ...contentTypeHeader,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
        ...getXClientHeaders(),
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
        ...(tempUserId && { 'X-Temp-User-ID': tempUserId }),
        ...getWorkspaceIdHeader(),
      },
      body,
      signal: abortController.signal,
      openWhenHidden: true,

      onopen: async (response) => {
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            const retryAfterSeconds =
              response.status === 429
                ? parseRetryAfterSeconds(response.headers.get('Retry-After'))
                : undefined;
            const errorText = await response.text();
            const httpError: ClassifiedStreamError = new Error(
              `HTTP ${response.status}: ${errorText || response.statusText}`
            );
            // Stamp the numeric status so downstream classification never has to
            // parse it back out of a message that may embed a PHI response body.
            httpError.httpStatus = response.status;
            if (retryAfterSeconds !== undefined) {
              httpError.retryAfterSeconds = retryAfterSeconds;
            }
            throw httpError;
          } else if (response.status >= 500) {
            logger.error('Chat timeline stream HTTP server error', {
              status: response.status,
              statusText: response.statusText,
              sessionId: sessionId ?? 'sessionless',
            });
            const httpError: ClassifiedStreamError = new Error(
              `Server error ${response.status}: ${response.statusText}`
            );
            httpError.httpStatus = response.status;
            throw httpError;
          }
        }
      },

      onmessage: (event) => {
        try {
          if (!event.data || event.data.trim() === '') {
            return;
          }

          // Any message (including heartbeats) resets the idle timer
          lastActivityAt = Date.now();
          lastEventTime = performance.now();
          chunksReceived += 1;

          // Check for keepalive signals before type-narrowing cast.
          // Heartbeats and processing events are backend keepalive signals
          // during long-running operations. They reset the idle timer above
          // but carry no displayable content.
          const raw = JSON.parse(event.data) as { type: string };
          // `raw.type` is a backend-controlled event discriminator (an enum-like
          // tag: 'text', 'message_info', 'heartbeat', …), never user content, so
          // it is safe to surface to RUM. Captured here — above the type guards —
          // deliberately, so `lastChunkType` reflects the genuine last chunk even
          // when it is a keepalive or the terminal sentinel. Any future event
          // whose `type` could encode user data must be sanitised before it
          // reaches this assignment.
          lastChunkType = raw.type;
          // The vocabulary gate. Every branch below still runs — an unknown tag
          // is REPORTED, not dropped — so a backend shipping a tag this build
          // has never seen stops being invisible. TAG ONLY: the payload can
          // carry PHI and never reaches the log line.
          const decoded = decodeAnswerPayload(raw);
          if (!decoded.known) {
            logger.warn('Unknown chat timeline chunk type', {
              chunkType: decoded.type,
              sessionId: sessionId ?? 'sessionless',
            });
          }
          if (raw.type === 'heartbeat' || raw.type === 'processing') {
            onKeepalive?.();
            return;
          }

          if (raw.type === 'capacity') {
            onCapacity?.(raw as CapacityChunk);
            return;
          }

          // EVI-2117 terminal sentinel: the last chunk of a fully-successful
          // stream. Latch it and swallow it — it is telemetry-only and carries
          // no renderable content, so it must never reach the timeline reducer.
          if (raw.type === 'stream_complete') {
            sawTerminalSentinel = true;
            return;
          }

          // In-band refusal (HTTP 200 + SSE error). Not a renderable chunk —
          // forwarding it used to close the turn as success and mint another
          // completion immediately.
          if (raw.type === 'error') {
            const inBand = raw as {
              error_type?: unknown;
              retry_after_seconds?: unknown;
            };
            const errorCode = errorCodeFromInBandType(
              typeof inBand.error_type === 'string' ? inBand.error_type : undefined
            );
            const retryAfterSeconds = parseRetryAfterSeconds(inBand.retry_after_seconds);
            const isBackoff =
              errorCode === 'rate_limited' || errorCode === 'concurrency_limited';
            terminalDelivered = true;
            onError(
              new Error(
                isBackoff
                  ? TIMELINE_STREAM_ERROR.RATE_LIMIT_EXCEEDED
                  : TIMELINE_STREAM_ERROR.QUERY_FAILED
              ),
              {
                lifecycle: buildLifecycle('onerror'),
                errorCode,
                ...(retryAfterSeconds !== undefined && { retryAfterSeconds }),
              }
            );
            return;
          }

          const parsed = raw as Chunk | ChatMetadataChunkUnion | SearchMetadataChunk;

          // Handle metadata chunks separately (e.g., chat title updates, search metadata)
          if (parsed.type === 'chat_metadata' || parsed.type === 'search_metadata') {
            onMetadata?.(parsed);
            return;
          }

          // Latch the backend's terminal-sentinel capability from message_info
          // (still forwarded so the reducer captures message_id as usual).
          if (
            parsed.type === 'message_info' &&
            parsed.supports_terminal_sentinel === true
          ) {
            capabilityAdvertised = true;
          }

          onChunk(parsed);
        } catch (parseError: unknown) {
          logger.error('Failed to parse chat timeline stream chunk', {
            error: parseError,
            sessionId: sessionId ?? 'sessionless',
            eventData: event.data?.substring(0, 200),
          });
          onError(parseError as Error, {
            lifecycle: buildLifecycle('onerror'),
            ...classifyStreamError(parseError, 'onerror'),
          });
        }
      },

      onclose: () => {
        clearIdleTimeout();
        if (closeFinalizationStarted) return;
        closeFinalizationStarted = true;
        terminalDelivered = true;
        // A capable backend that closed without its terminal sentinel was cut
        // off mid-answer — report truncation so the consumer stops mis-scoring
        // it as success. Pre-sentinel backends never advertise, so this stays
        // false and the turn is treated as a normal completion (EVI-2117).
        const truncated = capabilityAdvertised && !sawTerminalSentinel;
        // Fire immediately and hand the still-pending capture over: waiting on
        // resource timing here would stall the UI's finalization of the turn by
        // up to the full grace window.
        onComplete({
          truncated,
          ...(lastEventTime !== undefined && { lastEventTime }),
          networkTiming: networkTimingCapture.finish(),
          lifecycle: buildLifecycle('onclose'),
        });
      },

      onerror: (error) => {
        clearIdleTimeout();
        networkTimingCapture.cancel();
        terminalDelivered = true;
        const errorMessage = getErrorMessage(error);
        onError(new Error(errorMessage, { cause: error }), {
          lifecycle: buildLifecycle('onerror'),
          ...classificationWithRetryAfter(error, 'onerror'),
        });

        throw error;
      },
    });
  } catch (error) {
    networkTimingCapture.cancel();
    terminalDelivered = true;
    const errorMessage = getErrorMessage(error);
    logger.error('Chat timeline stream initialization error', {
      error,
      errorMessage,
      sessionId: sessionId ?? 'sessionless',
      url,
    });
    onError(new Error(errorMessage, { cause: error }), {
      lifecycle: buildLifecycle('onerror'),
      ...classificationWithRetryAfter(error, 'onerror'),
    });
  } finally {
    // @fortaine/fetch-event-source silently resolves on AbortController signals
    // without calling onclose/onerror. The finally block guarantees the idle
    // timer is always cleaned up, preventing orphaned setIntervals that would
    // otherwise fire false "response took too long" errors.
    clearIdleTimeout();
    // Only `onclose` hands the capture to a consumer, and it latches the flag
    // before doing so. Any other way out of the await — abort, a throw already
    // handled above, or a silent resolve — leaves nobody waiting on the
    // promise, so the observer must be torn down or it outlives the request.
    if (!closeFinalizationStarted) {
      networkTimingCapture.cancel();
    }
    // The gap that made the incident invisible: the promise settled
    // but no terminal reached the consumer. Without this, such a send has no
    // terminal telemetry until the 5-min global watchdog — and a genuine silent
    // resolve is never reported at all. `signal.aborted` distinguishes a
    // programmatic/user abort from an on-its-own resolve; the idle-timeout and
    // both error paths latch `terminalDelivered`, so they never reach here.
    if (!terminalDelivered) {
      onStreamSettled?.(
        buildLifecycle(abortController.signal.aborted ? 'abort' : 'silent_resolve')
      );
    }
  }
};
